import { NotebookConstants } from "constants/notebook-constants";
import { useAtom, useSetAtom } from "jotai";
import Notebook from "models/interfaces/notebook";
import NotebookRecord from "models/view-models/notebook-record";
import { useEffect, useMemo } from "react";
import { currentNotebookAtom } from "utilities/atoms/notebooks-atom";
import useNotebookParams from "utilities/hooks/domain/notebooks/use-notebook-params";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";

type UpdateCurrentNotebookValues = Partial<Omit<Notebook, "id">>;

interface UseCurrentNotebook {
    currentNotebook: NotebookRecord | null | undefined;
    errors: string[];
    isSharedNotebook: boolean;
    rename: (newName: string) => Promise<void>;
    removePage: (deletedPageId: number) => void;
    updateCurrentNotebook: (values: UpdateCurrentNotebookValues) => void;
}

const useCurrentNotebook = (): UseCurrentNotebook => {
    const setCurrentNotebook = useSetAtom(currentNotebookAtom);
    const { notebookId: currentNotebookId } = useNotebookParams();
    const [currentNotebook] = useAtom(currentNotebookAtom);
    const {
        errors,
        renameNotebook,
        resultObject: notebooks,
        updateNotebook,
        removePageFromNotebook,
        loaded: areNotebooksLoaded,
    } = useNotebooksAtom();

    useEffect(() => {
        if (
            areNotebooksLoaded &&
            currentNotebookId !== null &&
            !isNaN(currentNotebookId)
        ) {
            const notebook =
                notebooks?.find((n) => n.id === currentNotebookId) ?? undefined;
            setCurrentNotebook(notebook);
        }
    }, [areNotebooksLoaded, currentNotebookId, notebooks, setCurrentNotebook]);

    const rename = async (newName: string) => {
        return await renameNotebook(currentNotebook?.id!, newName);
    };

    const updateCurrentNotebook = (values: UpdateCurrentNotebookValues) => {
        if (currentNotebook == null) {
            return;
        }

        const updatedNotebook = currentNotebook.with(values);

        updateNotebook(updatedNotebook);
    };

    const removePage = (deletedPageId: number) => {
        removePageFromNotebook(deletedPageId, currentNotebook?.id!);
    };

    const isSharedNotebook = useMemo(() => {
        return currentNotebook?.id === NotebookConstants.SharedPageNotebookId;
    }, [currentNotebook?.id]);

    return {
        currentNotebook,
        errors,
        isSharedNotebook,
        removePage,
        rename,
        updateCurrentNotebook,
    };
};

export default useCurrentNotebook;
